import { motion } from "framer-motion";
import { Code, Globe, Server, Zap } from "lucide-react";
import ServiceCard from "@/components/ServiceCard";
import { useEffect } from "react";

const Services = () => {
  useEffect(() => {
    if (window.location.hash === '#thunder-fox-services') {
      const element = document.getElementById('thunder-fox-services');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, []);
  const services = [
    {
      icon: Code,
      title: "Web Development",
      description: "Custom websites and full-stack solutions built with modern technologies",
      features: [
        "React, Next.js, and TypeScript development",
        "Full-stack solutions with Node.js and Python",
        "Flask and FastAPI backend services",
        "Responsive and mobile-first design",
        "Progressive Web Applications (PWA)",
        "E-commerce and CMS integration",
      ],
    },
    {
      icon: Globe,
      title: "Domain Purchase & Setup",
      description: "Complete domain management from consultation to deployment",
      features: [
        "Domain name consultation and recommendations",
        "Domain registration and purchase",
        "DNS configuration and management",
        "SSL certificate integration",
        "Email setup and configuration",
        "Domain transfer assistance",
      ],
    },
    {
      icon: Server,
      title: "Hosting Solutions",
      description: "High-speed, secure cloud hosting for businesses of all sizes",
      features: [
        "Cloud hosting on reliable infrastructure",
        "Scalable solutions from small to enterprise",
        "99.9% uptime guarantee",
        "Automated backups and security",
        "CDN integration for faster loading",
        "24/7 technical support",
      ],
    },
  ];

  return (
    <main id="thunder-fox-services" className="min-h-screen py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <Zap className="w-8 h-8 text-primary" />
            <h1 className="text-5xl font-bold text-foreground">
              Our <span className="text-primary neon-glow">Thunder-Fast</span> Services
            </h1>
            <Zap className="w-8 h-8 text-primary" />
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Comprehensive digital solutions to power your business growth
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <ServiceCard {...service} />
            </motion.div>
          ))}
        </div>

        {/* Why Choose Us Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-16"
        >
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 md:p-12 border border-primary/30 electric-border">
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center">
              Why Choose Thunder Fox ?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  title: "Speed",
                  description: "Lightning-fast delivery with rapid development cycles",
                },
                {
                  title: "Trust",
                  description: "MSME registered for your peace of mind",
                },
                {
                  title: "Quality",
                  description: "Premium code standards and best practices guaranteed",
                },
                {
                  title: "Innovation",
                  description: "Cutting-edge technologies and modern frameworks",
                },
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-primary/10 electric-glow">
                    <Zap className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center py-16"
        >
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Ready to Power Up Your Business?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss how our services can help you achieve your digital goals
          </p>
          <a
            href="/contact"
            className="uiverse-button"
          >
            Get in Touch
          </a>
        </motion.section>
      </div>
    </main>
  );
};

export default Services;
